import { isPlatformBrowser, isPlatformServer, DOCUMENT } from '@angular/common';
import { Component, inject, OnDestroy, PLATFORM_ID } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { ClientService } from '@core-app/services/client.service';
import { BootstrapService } from '@core-app/services/bootstrap.service';
import { NotificationService } from '@core-app/services/notification.service';
import { X_SERVICE_ZONE } from '@core-app/tokens';
import {
    AnalyticsConfiguration,
    AnalyticsService,
    ApiService,
    APP_CONFIG,
    AppState,
    BrandColors,
    BrandingService,
    CardsService,
    CONFIG,
    ConfigService,
    FaviconService,
    pickContrastingTextColor,
    TranslateService,
    updateAppConfig,
    WindowService,
} from '@gallaghercp/core-frontend';
import { Store } from '@ngrx/store';
import { TranslateService as TranslateProvider } from '@ngx-translate/core';
import { filter, Subscription } from 'rxjs';

@Component({
    selector: 'core-root',
    template: `
        @if (bootstrapFailed()) {
            <core-maintenance></core-maintenance>
        } @else {
            <router-outlet></router-outlet>
        }
    `,
    standalone: false,
})
export class AppComponent implements OnDestroy {
    title = 'core-app';
    subscription: Subscription = new Subscription();

    readonly bootstrapFailed = inject(BootstrapService).failed;

    private translate = inject(TranslateService);
    private translateProvider = inject(TranslateProvider);
    private analytics = inject(AnalyticsService);
    private api = inject(ApiService);
    private windowService = inject(WindowService);
    private store = inject<Store<AppState>>(Store);
    private router = inject(Router);
    private updates = inject(SwUpdate);
    private snackBar = inject(MatSnackBar);
    private notification = inject(NotificationService);
    private uiBranding = inject(BrandingService);
    private zone = inject(X_SERVICE_ZONE);
    private platformId = inject(PLATFORM_ID);
    private favicon = inject(FaviconService);
    private document = inject(DOCUMENT);
    private config = inject(ConfigService);
    private client = inject(ClientService);

    constructor() {
        this.analytics.initialise({
            gtm: { id: this.config.get('gtm'), consent: 3 },
        } as AnalyticsConfiguration);

        if (this.zone && (isPlatformServer(this.platformId) || isPlatformBrowser(this.platformId))) {
            this.api.setServiceZone(Number(this.zone));
        }

        (this.config.get('domainsToZones') ?? []).forEach((mapping: any) => {
            if (this.windowService.location?.hostname === mapping.domain) {
                this.api.setServiceZone(mapping.zone);
            }
        });

        if (isPlatformBrowser(this.platformId)) {
            const currentPath = this.windowService.location?.pathname;
            if (currentPath === '/' || currentPath === '/home') {
                this.client.navigateToHome();
            }
        }

        const faviconPath = this.config.get('imagery')?.['favicon'] || '/assets/img/favicon.ico';
        this.favicon.setFavicon(faviconPath);

        const appFaviconEl = document.getElementById('appFavicon');
        if (appFaviconEl) {
            appFaviconEl.setAttribute('href', faviconPath);
        }

        const benefitPids = this.config.get('benefitPIDS');
        if (benefitPids) {
            CardsService.PIDS_BENEFITS = benefitPids ?? [2];
        }

        const miscPids = this.config.get('miscPIDS');
        if (miscPids) {
            CardsService.PIDS_LIFE_JOURNEYS = miscPids ?? [3];
        }

        const articlesParentRoute = this.config.get('articlesParentRoute');
        if (articlesParentRoute) {
            CardsService.ARTICLE_PATH = articlesParentRoute;
            CardsService.ARCHIVE_ARTICLE_PATH = articlesParentRoute + '-archive';
        }

        this.applyConfiguredStyles();

        this.subscription.add(
            this.store
                .select(state => state.app)
                .subscribe(() => {
                    this.applyConfiguredStyles();
                }),
        );

        // Apply top-nav presentation classes based on app.config booleans
        if (isPlatformBrowser(this.platformId)) {
            const body = document.body.classList;

            this.setButtonStyling(body);

            // New: includeBoxShadow/includeBottomBorder toggles for top navigation
            // Read only from navigation-scoped config, with safe legacy default.
            const navCfg = (this.config.get('navigation') ?? {}) as any;
            const includeBoxShadow = navCfg.includeBoxShadow as boolean | undefined;
            const includeBottomBorder = navCfg.includeBottomBorder as boolean | undefined;

            // Legacy default: if both are undefined, enable shadow by default
            const bothUndefined = typeof includeBoxShadow === 'undefined' && typeof includeBottomBorder === 'undefined';
            const shadow = bothUndefined ? true : includeBoxShadow === true; // only true when explicitly set, unless both undefined
            const border = includeBottomBorder === true; // only when explicitly set

            body.toggle('topnav-has-shadow', shadow);
            body.toggle('topnav-has-border', border);

            // Toggle sidepanel stickiness based on config
            const stickySidepanel = this.config.get('theme')?.sidePanel?.sticky !== false;
            body.toggle('benefit-sidepanel--no-sticky', !stickySidepanel);
        }

        if (this.config.isEnabled('multilingual', true)) {
            // The default language for Bedrocc is set as en_GB, but that may not be the default language for the specific instance.
            // Set local copies of the default & current language variables
            let defaultLanguageForApplication = this.translate.getDefaultLanguage();
            const currentLanguageForApplication = this.translate.getCurrentLanguage() || defaultLanguageForApplication;

            // Eagerly set default language and start loading current language immediately
            this.translateProvider?.setDefaultLang?.(defaultLanguageForApplication);
            if (
                defaultLanguageForApplication &&
                !this.translateProvider?.translations?.[defaultLanguageForApplication]
            ) {
                this.translateProvider?.setTranslation?.(defaultLanguageForApplication, {}, true);
            }
            this.translateProvider?.use?.(currentLanguageForApplication);

            // Register the available languages
            this.subscription.add(
                this.translate.getLanguages().subscribe((languages: any[]) => {
                    const languageCodes = languages.map((language: any) => language.code);
                    this.translateProvider?.addLangs?.(languageCodes);

                    // Validation: If the default isn't in the list, fallback to the first one
                    if (languageCodes.length > 0 && !languageCodes.includes(defaultLanguageForApplication)) {
                        defaultLanguageForApplication = languageCodes[0];
                        this.translateProvider?.setDefaultLang?.(defaultLanguageForApplication);
                    }

                    // Ensure the active language matches available options or falls back
                    if (languageCodes.length > 0) {
                        const targetLanguage = languageCodes.includes(currentLanguageForApplication)
                            ? currentLanguageForApplication
                            : languageCodes[0];
                        if (targetLanguage !== currentLanguageForApplication) {
                            this.translate.setCurrentLanguage(targetLanguage);
                            this.translateProvider?.use?.(targetLanguage);
                        }
                    }
                }),
            );
        }

        this.subscription.add(
            this.router.events
                .pipe(filter(event => event instanceof NavigationEnd))
                .subscribe((event: NavigationEnd) => {
                    const url = event.urlAfterRedirects;
                    if (!url.startsWith('/ret')) {
                        this.store.dispatch(updateAppConfig({ lastVisitedUrl: url } as Partial<CONFIG>));
                    }
                    const navigation = this.router.getCurrentNavigation();
                    const fromUrl = navigation?.extras?.state?.['from'];
                    this.store.dispatch(updateAppConfig({ fromUrl } as Partial<CONFIG>));

                    if (isPlatformBrowser(this.platformId)) {
                        setTimeout(() => {
                            const pageTop = this.document.getElementById('page-top');
                            if (pageTop) {
                                pageTop.focus();
                            }
                        }, 0);
                    }
                }),
        );

        const cookiePolicy = this.config.get('cookiePolicy');
        if (isPlatformBrowser(this.platformId) && cookiePolicy) {
            const script = document.createElement('script');
            script.src = '//policy.cookiereports.com/' + cookiePolicy + '_panel-en-gb.js';
            document.body.appendChild(script);
        }

        const pwaConfig = this.config.get('pwa');
        if (isPlatformBrowser(this.platformId) && navigator && pwaConfig?.enabled) {
            try {
                navigator.clearAppBadge();
            } catch (e) {
                console.warn(e);
            }

            if (this.updates.isEnabled) {
                this.subscription.add(
                    this.updates.versionUpdates.subscribe((evt: VersionEvent) => {
                        switch (evt.type) {
                            case 'VERSION_DETECTED':
                                console.log(`Downloading new app version: ${evt.version.hash}`);
                                break;
                            case 'VERSION_READY':
                                console.log(`Current app version: ${evt.currentVersion?.hash}`);
                                console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
                                try {
                                    navigator.setAppBadge(1);
                                } catch (e) {
                                    console.warn(e);
                                }
                                this.promptUser();
                                break;
                            case 'VERSION_INSTALLATION_FAILED':
                                console.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
                                break;
                        }
                    }),
                );
            }

            if (pwaConfig?.notifications) {
                // Register Firebase messaging service worker (must exist separately at public root)
                navigator.serviceWorker?.register('/firebase-messaging-sw.js', {
                    type: 'module',
                });

                // Modular Firebase Messaging handled in NotificationService
                this.notification.subscribeToNotifications();
                this.notification.requestPermission();
                this.notification.receiveMessage();
            }
        }

        const pillars = this.config.get('pillars');
        if (pillars) {
            CardsService.PILLARS = pillars as string[];
        }
    }

    private setButtonStyling(body: DOMTokenList) {
        const fullButtonConfig = this.config.get('buttons');
        const legacyButtonConfig = this.config.get('buttonStyle');

        // Handle button shape config
        // While sites are using the legacy option, prioritize that to avoid breaking changes
        const buttonStyle = legacyButtonConfig ?? fullButtonConfig?.buttonStyle ?? 'rounded';

        // Preserve existing button shape behavior using string config
        const val = typeof buttonStyle === 'string' ? buttonStyle.toLowerCase() : undefined;
        const shape = val === 'pill' || val === 'flat' || val === 'rounded' ? (val as string) : 'rounded';
        const setBodyBtnShape = (s: 'pill' | 'flat' | 'rounded') => {
            body.remove('btn-shape-pill', 'btn-shape-flat', 'btn-shape-rounded');
            body.add(`btn-shape-${s}`);
        };
        setBodyBtnShape(shape as any);

        // Handle full configuration for each button type
        if (fullButtonConfig) {
            (['primary', 'secondary', 'inverse'] as const).forEach(type => {
                const config = (fullButtonConfig as any)[type];
                if (config) {
                    (['default', 'hover', 'active', 'focus'] as const).forEach(state => {
                        const stateConfig = (config as any)[state];
                        if (stateConfig) {
                            // Loop through each state provided
                            Object.keys(stateConfig).forEach(prop => {
                                const val = (stateConfig as any)[prop];
                                if (val && val !== '') {
                                    // Create CSS variables using the button type and state
                                    const cssProp = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
                                    const stateSuffix = state === 'default' ? '' : `-${state}`;
                                    let propertyValue = val;

                                    if (
                                        (cssProp === 'internal-icon' || cssProp === 'external-icon') &&
                                        !val.startsWith('url(') &&
                                        val !== 'none'
                                    ) {
                                        propertyValue = `url("${val}")`;
                                    }

                                    document.documentElement.style.setProperty(
                                        `--config-button-${type}${stateSuffix}-${cssProp}`,
                                        propertyValue,
                                    );

                                    if (cssProp === 'internal-icon') {
                                        document.documentElement.style.setProperty(
                                            `--config-button-${type}${stateSuffix}-has-internal-icon`,
                                            "'true'",
                                        );
                                    }

                                    if (cssProp === 'external-icon') {
                                        document.documentElement.style.setProperty(
                                            `--config-button-${type}${stateSuffix}-has-external-icon`,
                                            "'true'",
                                        );
                                    }
                                }
                            });
                        }
                    });
                }
            });
        }
    }

    private applyConfiguredStyles(): void {
        const colors: BrandColors | null = this.uiBranding.getBrandColors();
        const configColors = this.config.get('colors') ?? (APP_CONFIG as any).colors;

        const headerBg: string | undefined =
            colors?.bg ?? (configColors as any)?.elementBackgroundColour ?? (configColors as any)?.headerBg;

        const navigationBg: string | undefined =
            (configColors as any)?.navBg ??
            colors?.bg ??
            (configColors as any)?.elementBackgroundColour ??
            (configColors as any)?.headerBg;

        const footerBg: string | undefined =
            (configColors as any)?.footerBg ?? colors?.bg ?? (configColors as any)?.elementBackgroundColour;

        const headerText: string | undefined = (configColors as any)?.headerText;

        if (headerBg) {
            document.documentElement.style.setProperty('--header-bg', headerBg);
        } else {
            document.documentElement.style.removeProperty('--header-bg');
        }

        if (headerText) {
            document.documentElement.style.setProperty('--header-text', headerText);
        } else {
            document.documentElement.style.removeProperty('--header-text');
        }

        const elementBackgroundColour: string | undefined = (configColors as any)?.elementBackgroundColour;
        if (elementBackgroundColour) {
            document.documentElement.style.setProperty('--element-background-colour', elementBackgroundColour);
            document.documentElement.style.setProperty('--element-background-color', elementBackgroundColour);
        } else {
            document.documentElement.style.removeProperty('--element-background-colour');
            document.documentElement.style.removeProperty('--element-background-color');
        }

        if (navigationBg) {
            const contrastColor = pickContrastingTextColor(navigationBg);
            const navLink = contrastColor === 'white' ? '#ffffff' : '#000000';
            const navLinkActive = navLink;

            document.documentElement.style.setProperty('--color-nav-bg', navigationBg);
            document.documentElement.style.setProperty('--color-bkg-pre-login', navigationBg);
            document.documentElement.style.setProperty('--color-bkg-pre-login-bg-content', contrastColor);
            document.documentElement.style.setProperty('--color-nav-link', navLink);
            document.documentElement.style.setProperty('--color-nav-link-active', navLinkActive);
        } else {
            document.documentElement.style.removeProperty('--color-nav-bg');
            document.documentElement.style.removeProperty('--color-bkg-pre-login');
            document.documentElement.style.removeProperty('--color-bkg-pre-login-bg-content');
            document.documentElement.style.removeProperty('--color-nav-link');
            document.documentElement.style.removeProperty('--color-nav-link-active');
        }

        if (footerBg) {
            const contrastColor = pickContrastingTextColor(footerBg);
            const footerLink = contrastColor === 'white' ? '#ffffff' : '#000000';

            document.documentElement.style.setProperty('--color-footer-bg', footerBg);
            document.documentElement.style.setProperty('--footer-bg', footerBg);
            document.documentElement.style.setProperty('--color-footer-text', footerLink);
            document.documentElement.style.setProperty('--footer-text', footerLink);
        } else {
            document.documentElement.style.removeProperty('--color-footer-bg');
            document.documentElement.style.removeProperty('--footer-bg');
            document.documentElement.style.removeProperty('--color-footer-text');
            document.documentElement.style.removeProperty('--footer-text');
        }

        const footerDivider: string | undefined =
            this.config.get('colors')?.['footerDivider'] ?? (configColors as any)?.footerTopBorder;
        if (footerDivider) {
            document.documentElement.style.setProperty('--footer-top-border', footerDivider);
        }

        const footerLinkColor: string | undefined = (configColors as any)?.footerLinkColor;
        if (footerLinkColor) {
            document.documentElement.style.setProperty('--footer-link-color', footerLinkColor);
        } else if (footerBg) {
            const contrastColor = pickContrastingTextColor(footerBg);
            const footerLink = contrastColor === 'white' ? '#ffffff' : '#000000';
            document.documentElement.style.setProperty('--footer-link-color', footerLink);
        } else {
            document.documentElement.style.removeProperty('--footer-link-color');
        }

        const utilityBelowTopBorder: string | undefined =
            this.config.get('theme')?.navigation?.utilitiesBelowTopBorderColor ??
            (configColors as any)?.utilitiesBelowTopBorderColor ??
            (configColors as any)?.utilityBelowTopBorder;
        if (utilityBelowTopBorder) {
            document.documentElement.style.setProperty(
                '--utility-below-top-border',
                `1px solid ${utilityBelowTopBorder}`,
            );
        }

        const utilityAboveBottomBorder: string | undefined =
            this.config.get('theme')?.navigation?.utilitiesAboveBottomBorderColor ??
            (configColors as any)?.utilitiesAboveBottomBorderColor ??
            (configColors as any)?.utilityAboveBottomBorder;
        if (utilityAboveBottomBorder) {
            document.documentElement.style.setProperty(
                '--utility-above-bottom-border',
                `1px solid ${utilityAboveBottomBorder}`,
            );
        }

        const headingTextColor: string | undefined = (configColors as any)?.headingText;
        if (headingTextColor) {
            document.documentElement.style.setProperty('--heading-text', headingTextColor);
        }

        const subheadingTextColor: string | undefined = (configColors as any)?.subheadingText;
        if (subheadingTextColor) {
            document.documentElement.style.setProperty('--subheading-color', subheadingTextColor);
            document.documentElement.style.setProperty('--subheading-text', subheadingTextColor);
        } else {
            document.documentElement.style.removeProperty('--subheading-color');
            document.documentElement.style.removeProperty('--subheading-text');
        }

        const tableHeadingBg: string | undefined = (configColors as any)?.tableHeadingBg;
        if (tableHeadingBg) {
            document.documentElement.style.setProperty('--table-heading-bg', tableHeadingBg);
        } else {
            document.documentElement.style.removeProperty('--table-heading-bg');
        }

        const tableSubheadingBg: string | undefined = (configColors as any)?.tableSubheadingBg;
        if (tableSubheadingBg) {
            document.documentElement.style.setProperty('--table-subheading-bg', tableSubheadingBg);
        } else {
            document.documentElement.style.removeProperty('--table-subheading-bg');
        }

        const accordionTheme = this.config.get('theme')?.['accordion'];
        const accordionTitleBgColor: string | undefined =
            accordionTheme?.titleBgColor ?? (accordionTheme as any)?.['--accordion-title-bg-color'];
        if (accordionTitleBgColor) {
            document.documentElement.style.setProperty('--accordion-title-bg-color', accordionTitleBgColor);
        } else {
            document.documentElement.style.removeProperty('--accordion-title-bg-color');
        }

        const accordionTitleBorderColor: string | undefined =
            accordionTheme?.titleBorderColor ?? (accordionTheme as any)?.['--accordion-title-border-color'];
        if (accordionTitleBorderColor) {
            document.documentElement.style.setProperty('--accordion-title-border-color', accordionTitleBorderColor);
        } else {
            document.documentElement.style.removeProperty('--accordion-title-border-color');
        }

        const accordionTitleOpenBorderColor: string | undefined =
            accordionTheme?.titleOpenBorderColor ?? (accordionTheme as any)?.['--accordion-title-open-border-color'];
        if (accordionTitleOpenBorderColor) {
            document.documentElement.style.setProperty(
                '--accordion-title-open-border-color',
                accordionTitleOpenBorderColor,
            );
        } else {
            document.documentElement.style.removeProperty('--accordion-title-open-border-color');
        }

        const accordionTitleOpenBgColor: string | undefined =
            accordionTheme?.titleOpenBgColor ?? (accordionTheme as any)?.['--accordion-title-open-bg-color'];
        if (accordionTitleOpenBgColor) {
            document.documentElement.style.setProperty('--accordion-title-open-bg-color', accordionTitleOpenBgColor);
        } else {
            document.documentElement.style.removeProperty('--accordion-title-open-bg-color');
        }

        const tableConfig = (this.config.get('tables') ?? {}) as any;
        if (tableConfig?.headingFontSize) {
            document.documentElement.style.setProperty('--table-heading-font-size', tableConfig.headingFontSize);
        } else {
            document.documentElement.style.removeProperty('--table-heading-font-size');
        }

        if (tableConfig?.tdUlMarginBottom) {
            document.documentElement.style.setProperty('--table-td-ul-margin-bottom', tableConfig.tdUlMarginBottom);
        } else {
            document.documentElement.style.removeProperty('--table-td-ul-margin-bottom');
        }

        const navConfig = this.config.get('navigation');
        if (navConfig?.navbarBrandWidth) {
            document.documentElement.style.setProperty('--brand-logo-width', navConfig.navbarBrandWidth);
        }

        const headingsConfig = this.config.get('typography')?.headings;
        if (headingsConfig) {
            Object.keys(headingsConfig).forEach(key => {
                const config = (headingsConfig as any)[key];
                if (config.fontSize) {
                    document.documentElement.style.setProperty(`--${key}-font-size`, config.fontSize);
                }
                if (config.fontWeight) {
                    document.documentElement.style.setProperty(`--${key}-font-weight`, config.fontWeight);
                }
                if (config.lineHeight) {
                    document.documentElement.style.setProperty(`--${key}-line-height`, config.lineHeight);
                }
                if (config.letterSpacing) {
                    document.documentElement.style.setProperty(`--${key}-letter-spacing`, config.letterSpacing);
                }
            });
        }

        const listBullet: string | undefined = (configColors as any)?.listBullet ?? (configColors as any)?.primary;
        if (listBullet) {
            document.documentElement.style.setProperty('--list-bullet', listBullet);
        }

        const actionButtonBorderRadius = (this.config.get('layout') as any)?.actionBorderRadius;
        if (actionButtonBorderRadius) {
            const actionButtonBorderRadiusOverrideStyles = document.createElement('style');
            document.head.appendChild(actionButtonBorderRadiusOverrideStyles);

            // Convoluted selector to override specificity for core-button's
            // styles w/o introducing !important
            actionButtonBorderRadiusOverrideStyles.textContent = `
                :root .core-button.core-button[class*='core-button--'] {
                    --btn-border-radius: ${actionButtonBorderRadius};
                }
            `;
        }
    }

    promptUser(): void {
        const snackBarRef = this.snackBar.open('A new version of Core Bedrocc is available', 'Refresh');
        snackBarRef.onAction().subscribe(() => {
            this.windowService.location?.reload();
        });
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
